import type { WorkspaceFeatures } from "@expert/features";
import type { Logger } from "@expert/logging";

import { useFeatures } from "@expert/features";
import { useVerifiedCustomerStore } from "@expert/sales-tools";
import {
    type CloseGuideSource,
    type EndedSessionProps,
    type OpenGuideSource,
    useGuideSession,
} from "@expert/solve-tools";
import { useEffect, useMemo } from "react";

import { useIVRInfo } from "@/customer-info/api";
import { type usePartner, useSessionId, type VoiceTask } from "@/sdk";
import { sdkEventBus } from "@/sdk/agent/eventBus";

import { shouldOpenActivationGuide } from "../device-activation/shouldOpenActivationGuide";
import { shouldOpenInstallationGuide } from "../installation/shouldOpenInstallationGuide";
import { shouldOpenOnCallStart } from "../shouldOpenOnCallStart";

interface UseAutomaticGuideProps {
    activeVoiceTask: VoiceTask | null;
    isCustomerSession: boolean;
    isCustomerVerificationComplete: boolean;
    isGuideOpen: boolean;
    logger: Logger;
    partner: ReturnType<typeof usePartner>;
    closeGuide: (source: CloseGuideSource, endedSession?: EndedSessionProps) => void;
    openGuide: (source: OpenGuideSource, guide?: string) => void;
}

// TODO: [EWS-1449](https://solutonashville.atlassian.net/browse/EWS-1449)
// redesign how we handle automatic guide opening ^
export const useAutomaticGuide = ({
    activeVoiceTask,
    isCustomerSession,
    isCustomerVerificationComplete,
    isGuideOpen,
    logger,
    partner,
    closeGuide,
    openGuide,
}: Readonly<UseAutomaticGuideProps>) => {
    const sessionId = useSessionId();
    const { disableAutomaticOpen, isLoadingSimplrSession } = useGuideSession(sessionId);
    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const { intent } = useIVRInfo();
    const {
        features: { isDeviceActivationGuideIntentAutoOpenEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const { supportNumberCalled, routingQueue: queueName } = activeVoiceTask || {};

    useEffect(() => {
        const sessionChangedEvent = sdkEventBus.on("session_changed", ({ action, session: changedSession }) => {
            // Only auto-close when the guide's own session ends; ignore other chats starting/closing.
            if (!isGuideOpen || action !== "end" || changedSession.id !== sessionId) {
                return;
            }
            const latestTask = Array.from(changedSession.tasks?.values() ?? []).at(-1) as VoiceTask;
            const callSid = latestTask?.agentCallId;
            closeGuide("auto", { endedSessionId: changedSession.id, endedSessionCallSid: callSid });
        });
        return () => {
            sessionChangedEvent();
        };
    }, [closeGuide, isGuideOpen, sessionId]);

    // Values reused across all auto open use cases
    const shouldOpenDeps = useMemo(
        () => ({
            disableAutomaticOpen,
            isCustomerSession,
            isLoadingSimplrSession,
        }),
        [disableAutomaticOpen, isCustomerSession, isLoadingSimplrSession],
    );
    const shouldOpenArgs = useMemo(
        () => ({
            ...shouldOpenDeps,
            isGuideOpen,
        }),
        [isGuideOpen, shouldOpenDeps],
    );

    // Device Activation Guide auto-open
    useEffect(() => {
        const { shouldOpenGuide, sourceDetails } = shouldOpenActivationGuide({
            ...shouldOpenArgs,
            isDeviceActivationGuideIntentAutoOpenEnabled,
            partner,
            sessionId,
            supportNumberCalled,
            queueName,
            intent,
        });
        if (shouldOpenGuide && sourceDetails) {
            logger.info(
                {
                    ...shouldOpenArgs,
                    isDeviceActivationGuideIntentAutoOpenEnabled,
                    partner,
                    supportNumberCalled,
                    queueName,
                    intent,
                    sourceDetails,
                },
                "Solve Guide | Auto opening device activation guide",
            );
            openGuide({ source: "auto", sourceDetails }, "Device Activation");
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [
        intent,
        isDeviceActivationGuideIntentAutoOpenEnabled,
        partner,
        sessionId,
        shouldOpenDeps,
        supportNumberCalled,
        queueName,
    ]);

    // Installation Guide auto-open
    useEffect(() => {
        const { shouldOpenGuide, sourceDetails } = shouldOpenInstallationGuide({
            ...shouldOpenArgs,
            intent: customer?.intent,
            isCustomerVerificationComplete,
            sessionId,
        });
        if (shouldOpenGuide) {
            logger.info(
                { ...shouldOpenArgs, intent: customer?.intent, isCustomerVerificationComplete, sourceDetails },
                "Solve Guide | Auto opening installation guide",
            );
            openGuide({ source: "auto", sourceDetails }, "Installation");
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [customer?.intent, isCustomerVerificationComplete, sessionId, shouldOpenDeps, partner]);

    // on call start auto-open
    // TODO: [EWS-1449](https://solutonashville.atlassian.net/browse/EWS-1449)
    useEffect(() => {
        const shouldOpenGuide = shouldOpenOnCallStart({
            ...shouldOpenArgs,
            sessionId,
        });
        if (shouldOpenGuide) {
            logger.info({ ...shouldOpenArgs }, "Solve Guide | Auto opening ADT guide");
            openGuide({ source: "auto", sourceDetails: "call-start" }, "ADT Guide");
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [sessionId, shouldOpenDeps]);
};
